import { useEffect, useRef, useState } from "react";
import { ReactComponent as Bulb } from "@assets/images/bulb.svg";
import { ReactComponent as GreenTick } from "@assets/images/greenTick.svg";
import { ReactComponent as WhiteTick } from "@assets/images/WhiteTick.svg";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useTranslation } from "react-i18next";
import WeeklyPracticeGoal from "./WeeklyPracticeGoal";
import { markMindfulnessAsDone } from "api/mindfulness";
import { useUserData } from "context/OAuth/AuthContext";
import { useNavigate } from "react-router-dom";
import MindFulnessCardView from "./MindFulnessCardView";
import AlertDialog from "@components/ui/ConfirmDialogue";

const DashboardSuggestion = ({
  adviseData,
  setAdviseData,
  setFilterRecord,
  tab,
  isPlanLoading,
}) => {
  const { t } = useTranslation();
  const { subscription } = useUserData();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [marAsDoneLoading, setMarkAsDoneLoading] = useState(false);
  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const itemRefs = useRef([]);
  const [open, setOpen] = useState(false);
  // const todayIndex =
  //   adviseData?.findIndex((item) => item.date === today) !== -1
  //     ? adviseData?.findIndex((item) => item.date === today)
  //     : 0;

  const todayIndex = (() => {
    const byDate = adviseData?.findIndex((item) => item.date === today);
    if (byDate !== -1) return byDate;

    const byDay = adviseData?.findIndex((item) => item.day === todayName);
    if (byDay !== -1) return byDay;

    return 0;
  })();

  // For Daily Overview, always show today's index
  // For Weekly Overview, allow navigation through all days
  const [activeIndex, setActiveIndex] = useState(todayIndex);

  // Reset to today's index when switching to Daily Overview
  useEffect(() => {
    if (tab === "Daily Overview") {
      setActiveIndex(todayIndex);
    }
  }, [tab, todayIndex]);

  const days = adviseData?.map((item) => item.date);
  const activeDate =
    adviseData?.[activeIndex]?.date &&
    new Date(adviseData?.[activeIndex].date).toISOString().split("T")[0];
  const canMarkAsDone = activeDate && activeDate <= today;

  const handleMarkDone = async () => {
    setMarkAsDoneLoading(true);
    const activeItem = adviseData?.[activeIndex];
    if (!activeItem) return;

    try {
      await markMindfulnessAsDone({
        date: activeItem.date,
        title: activeItem.title,
      });

      const filteredChangedRecord = adviseData?.find(
        (item, idx) => idx === activeIndex
      );
      setFilterRecord(filteredChangedRecord);
      setAdviseData((prev) =>
        prev.map((item, idx) =>
          idx === activeIndex ? { ...item, mark_as_done: true } : item
        )
      );

      toast.info(t("toast.statusUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(t("toast.failUpdated"));

      // Rollback if API fails
      setAdviseData((prev) =>
        prev.map((item, idx) =>
          idx === activeIndex ? { ...item, mark_as_done: false } : item
        )
      );
    } finally {
      setMarkAsDoneLoading(false);
    }
  };

  const handleClose = (key = null) => {
    if (key !== "cancel") {
      handleMarkDone();
      setOpen(false);
    } else {
      setOpen(false);
    }
  };
  useEffect(() => {
    if (itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  // Render Daily Overview - Just today's advice
  if (tab === "Daily Overview") {
    return (
      <div className="bg-white shadow-lg p-6 rounded-[17px] w-full max-w-full bg-cover h-[100%]">
        <div className="flex justify-end items-center mb-4">
          {!isPlanLoading && (
            <button
              disabled={isPlanLoading}
              onClick={() =>
                subscription?.status === "active" ||
                subscription?.status === "pending-cancel"
                  ? navigate("/mindfulness-preferences", {
                      state: { mindfulness: true },
                    })
                  : navigate("/renew-subscription")
              }
              className={`${
                isPlanLoading && "opacity-70"
              } px-[20px] py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] font-[600] text-[#fff] rounded-[100px] text-[12px] `}
            >
              {t("Recreate Plan")}
            </button>
          )}
        </div>

        <div className="rounded-[17px] bg-white font-[Lato]">
          {/* No day selector for Daily Overview */}

          <div className="mt-2 md:m-4 md:border md:border-[#E6E6E6] rounded-[17px] md:p-2 md:p-4">
            <div className="flex flex-row md:items-center justify-between gap-2 mb-5">
              <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                  <Bulb />
                </p>{" "}
                {new Date(adviseData?.[activeIndex]?.date).toDateString() ===
                new Date().toDateString()
                  ? t("Today")
                  : t(`days.${adviseData?.[activeIndex]?.day?.toLowerCase()}`)}
                ’s {/* {t("Advice")} */}
                {t("mindfulnessCoachPreferences.Practice")}
              </button>

              {canMarkAsDone &&
                (adviseData?.[activeIndex]?.mark_as_done ? (
                  <button className="text-[#fff] cursor-default text-[12px] md:text-[14px] border border-[#36F136] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                    <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                      <GreenTick />
                    </p>
                  </button>
                ) : (
                  <button
                    className="text-[#fff] cursor-pointer text-[12px] md:text-[16px] font-[600] bg-[#36F136] rounded-full px-4 py-1 flex items-center justify-center gap-2 min-w-[136px] min-h-[36px]"
                    onClick={() => setOpen(true)}
                    disabled={marAsDoneLoading}
                  >
                    {marAsDoneLoading ? (
                      <ButtonLoader />
                    ) : (
                      <>
                        {/* <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                          <GreenTick />
                        </p>{" "} */}
                        <span>
                          {t("mindfulnessCoachPreferences.mark as done")}
                        </span>
                        <WhiteTick />
                      </>
                    )}
                  </button>
                ))}
            </div>

            <p className="text-[#000] font-[600] text-[18px]">
              {adviseData?.[activeIndex]?.title}
            </p>

            <p className="text-[#7B7B7B] text-[18px] text-base whitespace-pre-line">
              {adviseData?.[activeIndex]?.description}
            </p>

            {adviseData?.[activeIndex]?.description_steps && (
              <MindFulnessCardView
                mindfulnessStepsData={
                  adviseData?.[activeIndex]?.description_steps
                }
              />
            )}
          </div>
        </div>

        {/* Optional: Show Weekly Practice Goal only in Daily Overview if needed */}
        {/* <WeeklyPracticeGoal adviseData={adviseData} /> */}
        <WeeklyPracticeGoal adviseData={adviseData} />
      </div>
    );
  }

  // Render Weekly Overview - Full week navigation
  return (
    <div className="bg-white shadow-lg p-6 rounded-[17px] w-full max-w-full bg-cover h-[100%]">
      <div className="flex justify-end items-center mb-4">
        <button
          onClick={() =>
            subscription?.status === "active" ||
            subscription?.status === "pending-cancel"
              ? navigate("/mindfulness-preferences", {
                  state: { mindfulness: true },
                })
              : navigate("/renew-subscription")
          }
          className={`px-[20px] py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] font-[600] text-[#fff] rounded-[100px] text-[12px] `}
        >
          {t("Recreate Plan")}
        </button>
      </div>

      <div className="rounded-[17px] bg-white font-[Lato]">
        {/* Day selector for Weekly Overview */}
        <div className="flex items-center md:gap-2 w-full p-2 rounded-[11px] overflow-x-auto bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)]">
          {days?.map((item, index) => {
            return (
              <div
                key={index}
                className={`flex flex-col min-w-[80px] md:flex-1 rounded-[11px] !py-[28px] md:!px-[10px] cursor-pointer border border-[transparent] gap-[10px] text-white
                            ${
                              activeIndex === index
                                ? "bg-white !text-black "
                                : "hover:border hover:border-[#fff] hover:text-[#fff]"
                            }
                            `}
                ref={(el) => (itemRefs.current[index] = el)}
                onClick={() => setActiveIndex(index)}
              >
                <p className="text-[14px] font-[Lato] font-[400] text-center">
                  {t(
                    `dietPlanDashboard.allMeals.daysShort.${new Date(
                      item
                    ).toLocaleDateString("en-US", {
                      weekday: "short",
                    })}`
                  )}
                </p>

                <p className="text-[16px] md:text-[22px] font-[600] font-[Lato] text-center">
                  {format(item, "dd MMM")}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-2 md:m-4 md:border md:border-[#E6E6E6] rounded-[17px] md:p-4">
          <div className="flex flex-row md:items-center justify-between gap-2 mb-5">
            <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
              <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                <Bulb />
              </p>{" "}
              {new Date(adviseData?.[activeIndex]?.date).toDateString() ===
              new Date().toDateString()
                ? t("TodayExercise")
                : `${t(
                    `days.${adviseData?.[activeIndex]?.day?.toLowerCase()}`
                  )} ’s ${t("Practice")}`}
              {/* ’s {" "} */}
              {/* {t("Advice")} */}
              {/* {t("mindfulnessCoachPreferences.Practice")} */}
            </button>

            {canMarkAsDone &&
              (adviseData?.[activeIndex]?.mark_as_done ? (
                <button className="text-[#fff] cursor-default text-[12px] md:text-[14px] border border-[#36F136] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    <GreenTick />
                  </p>
                </button>
              ) : (
                <button
                  className="text-[#fff] cursor-pointer text-[12px] md:text-[16px] font-[600] bg-[#36F136] rounded-full px-4 py-1 flex items-center justify-center gap-2 min-w-[136px] min-h-[36px]"
                  onClick={() => setOpen(true)}
                  disabled={marAsDoneLoading}
                >
                  {marAsDoneLoading ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      {/* <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                        <GreenTick />
                      </p>{" "} */}
                      <span>
                        {t("mindfulnessCoachPreferences.mark as done")}
                      </span>
                      <WhiteTick />
                    </>
                  )}
                </button>
              ))}
          </div>

          <p className="text-[#000] font-[600] text-[18px]">
            {adviseData?.[activeIndex]?.title}
          </p>

          <p className="text-[#7B7B7B] text-[18px] text-base whitespace-pre-line">
            {adviseData?.[activeIndex]?.description}
          </p>

          {adviseData?.[activeIndex]?.description_steps && (
            <MindFulnessCardView
              mindfulnessStepsData={
                adviseData?.[activeIndex]?.description_steps
              }
            />
          )}
        </div>
      </div>

      <WeeklyPracticeGoal adviseData={adviseData} />

      <AlertDialog
        open={open}
        // handleClickOpen={handleClickOpen}
        handleClose={handleClose}
        isLoading={marAsDoneLoading}
        heading={t("mindfulnessCoachPreferences.Complete Practice")}
        message={t(
          "mindfulnessCoachPreferences.Would you like to mark this session as completed?"
        )}
      />
    </div>
  );
};

export default DashboardSuggestion;
